import type {} from '@mui/lab/themeAugmentation';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import type {} from '@mui/x-data-grid/themeAugmentation';
import { Palette, createTheme } from '@mui/material';

declare module '@mui/material/styles' {
  interface OtherPalette {
    snackbar: string;
    ratingActive: string;
  }
  interface SurfacesPalette {
    background: string;
    backgroundPanel: string;
    panel: string;
    selected: string;
  }
  interface Theme {
    palette: Palette & PaletteOptions;
  }

  interface PaletteOptions {
    other: OtherPalette;
    surfaces: SurfacesPalette;
  }
}

const theme = createTheme({
  components: {
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
        variant: 'outlined',
      },
      styleOverrides: {
        colorPrimary: {
          backgroundColor: '#001B39', // Surfaces/background
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          backgroundColor: '#001B39', // Surfaces/background
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          flexShrink: 0,
          whiteSpace: 'nowrap',
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: { borderRadius: '9999px !important' },
      },
    },
    MuiCard: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          borderRadius: '8px',
        },
      },
    },
    MuiCardActionArea: {
      styleOverrides: {
        root: {
          background: '#FFFFFF',
          // Reset overrides from MuiButtonBase
          borderRadius: '4px !important',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          ':last-child': {
            paddingBottom: '16px',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme }) => ({ color: theme.palette.text.secondary }),
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        cell: {
          '&:focus, :focus-within': {
            outline: 'none',
          },
        },
        columnHeader: {
          ':focus-within': {
            outline: 'none',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: 16,
        },
      },
    },
    MuiDialogTitle: {
      defaultProps: {
        variant: 'h5',
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: '8px !important',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: ({ theme }) => ({ color: theme.palette.primary.dark, textUnderlineOffset: 4 }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: { borderRadius: 'inherit !important' }, // Disable MuiButtonBase styles
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'inherit',
          borderRadius: '0 !important',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 'bold',
          paddingBottom: 12,
          paddingTop: 12,
        },
      },
    },
    MuiTabPanel: {
      styleOverrides: {
        root: {
          paddingRight: 0,
          paddingLeft: 0,
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: ({ theme: { transitions } }) => ({
          '.MuiTabs-indicator': {
            transition: [
              transitions.create('all', {
                duration: transitions.duration.standard,
                easing: transitions.easing.easeInOut,
              }),
              'visibility 0ms',
            ].join(', '),
          },
        }),
      },
    },
  },
  typography: palette => ({
    fontFamily: 'Lato',
    fontWeightBold: 700,
    h1: { fontWeight: 'bold', fontSize: '3rem' },
    h2: { fontWeight: 'bold', fontSize: '2.5rem' },
    h3: { fontWeight: 'bold', fontSize: '2rem', lineHeight: 1 },
    h4: { fontWeight: 'bold', fontSize: '1.5rem', lineHeight: 1 },
    h5: { fontWeight: 'bold', fontSize: '1.25rem' },
    h6: { fontWeight: 'bold', fontSize: '1rem', lineHeight: '1.125rem' },
    overline: { lineHeight: '2rem', color: palette.text.secondary },
    subtitle2: { fontWeight: 700 },
  }),
  palette: {
    background: {
      default: '#FAFAFA',
      paper: '#FFFFFF',
    },
    surfaces: {
      background: '#001B39',
      backgroundPanel: '#F6F6F6',
      panel: '#F2F6FD', // alpha('#7CACF8', 0.08),
      selected: '#E2E5E9',
    },
    primary: {
      main: '#5A6EDD',
      dark: '#4A5BBB',
      light: '#8492EA',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#9E845C',
      dark: '#775E41',
      light: '#C2AA8F',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#00B883',
      dark: '#1B5E20',
      light: '#4CAF50',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#06ACA1',
      dark: '#01579B',
      light: '#03A9F4',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#FF7A00',
      dark: '#E65100',
      light: '#FF9800',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#DC0801',
      light: '#EF5350',
      dark: '#C62828',
      contrastText: '#FFFFFF',
    },
    other: {
      snackbar: '#323232',
      ratingActive: '#FFB400',
    },
  },
});

export default theme;
